/*
 * This is the entry point for all signed in (My pages) related functionality
 */

// libs
import React from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import styled from 'styled-components';
import {Provider} from 'react-redux';
import moment from 'moment';
import 'moment/locale/sv';

// react "pages"
import SignedInStartPage from './Lararforsakringar/Pages/SignedInStartPage/SignedInStartPage';
import ProfilePage from './Lararforsakringar/Pages/ProfilePage/ProfilePage';
import TravelCertificatePage from './Lararforsakringar/Pages/TravelCertificatePage/TravelCertificatePage';
import PensionGuidanceQuestionPage from './Lararforsakringar/Pages/PensionGuidanceQuestionPage/PensionGuidanceQuestionPage';

import InsuranceHealthDeclarationPage from './Lararforsakringar/Pages/InsuranceHealthDeclarationPage/InsuranceHealthDeclarationPage';
import InsurancePreregistrationPage from './Lararforsakringar/Pages/InsurancePreregistrationPage/InsurancePreregistrationPage';

import AchievementsPage from './Lararforsakringar/Pages/Achievements/Components/AchievementsPage';

import CustomerInfoPage from './Lararforsakringar/Pages/CustomerInfoPage/CustomerInfoPage';

// react features
import ExtendStudentInsuranceTeaser from './Lararforsakringar/Features/ExtendStudentInsuranceTeaser/ExtendStudentInsuranceTeaser';
import SignedInPossessionsListing from './Lararforsakringar/Features/SignedInPossessionsListing/SignedInPossessionsListing';
import PurchasesStatusTeaser from './Lararforsakringar/Features/PurchasesStatusTeaser/PurchasesStatusTeaser';
import CoInsuredForm from './Lararforsakringar/Features/CoInsuredFormBlock/Components/CoInsuredForm';

import AchievementsHeroAnimation from './Lararforsakringar/Features/AnimatedHeros/AchievementsHeroAnimation';
import AnimatedUspTeaserSchoolDirectorsHeadset from './Lararforsakringar/Features/AnimatedUspTeaser/Components/AnimatedUspTeaserSchoolDirectorsHeadset';
import AnimatedUspTeaserCustomerSurvey from './Lararforsakringar/Features/AnimatedUspTeaser/Components/AnimatedUspTeaserCustomerSurvey';
import AnimatedUspTeaserManagedPension from './Lararforsakringar/Features/AnimatedUspTeaser/Components/AnimatedUspTeaserManagedPension';

import Announcers from './Lararforsakringar/Features/Announcers/Announcers';
import BasicInsuranceCoverList from './Lararforsakringar/Features/BasicInsuranceCoverBlock/Components/BasicInsuranceCoverList';

import WizardBase from './Lararforsakringar/Features/CustomerKnowledgeWizardBlock/Components/WizardBase';
import FormContainer from './Lararforsakringar/Features/PensionInsuranceWizardBlock/Components/Views/FormContainer';
import SignatureApp from './Lararforsakringar/Features/SignatureProcessBlock/SignatureApp';

// store
import {configureStore} from './Lararforsakringar/State/myPagesStore';



// react mount roots
const sections = [
    { id: 'mypages-start-root', component: <SignedInStartPage mountRootId="mypages-start-root" />},
    { id: 'mypages-insurance-preregistration-root', component: <InsurancePreregistrationPage mountRootId="mypages-insurance-preregistration-root" />},
    { id: 'mypages-insurance-health-declaration-root', component: <InsuranceHealthDeclarationPage mountRootId="mypages-insurance-health-declaration-root" />},
    { id: 'mypages-profile-root', component: <ProfilePage mountRootId="mypages-profile-root" />},
    { id: 'mypages-travel-cert-root', component: <TravelCertificatePage mountRootId="mypages-travel-cert-root" />},
    { id: 'mypages-pension-guidance-root', component: <PensionGuidanceQuestionPage mountRootId="mypages-pension-guidance-root" />},
    { id: 'mypages-customer-info-root', component: <CustomerInfoPage mountRootId="mypages-customer-info-root" /> },
    { id: 'mypages-possessions-listing', component: <SignedInPossessionsListing mountRootId="mypages-possessions-listing" /> },
    { id: 'mypages-preregistration-summary-result', component: <PurchasesStatusTeaser mountRootId="mypages-preregistration-summary-result" /> },
    { id: 'mypages-student-special-teaser-root', component: <ExtendStudentInsuranceTeaser mountRootId="mypages-student-special-teaser-root" /> },
    { id: 'coinsured-form-react-app-root', component: <CoInsuredForm mountRootId="coinsured-form-react-app-root" /> },
    { id: 'announcer-react-root', component: <Announcers mountRootId="announcer-react-root" /> },
    { id: 'achievements-react-app-root', component: <AchievementsPage mountRootId="achievements-react-app-root" /> },
    { id: 'achievements-animated-hero-react-app-root', component: <AchievementsHeroAnimation mountRootId="achievements-animated-hero-react-app-root" /> },
    { id: 'coinsured-form-react-app-root', component: <CoInsuredForm mountRootId="coinsured-form-react-app-root" /> },
    { id: 'basic-insurance-cover-react-app-root', component: <BasicInsuranceCoverList mountRootId="basic-insurance-cover-react-app-root" /> },
    { id: 'managed-pension-insurance-wizard-block-react-app-root', component: <FormContainer mountRootId="managed-pension-insurance-wizard-block-react-app-root" /> },
    { id: 'signature-process-block-react-app-root', component: <SignatureApp mountRootId="signature-process-block-react-app-root" /> },

    { class: 'customer-knowledge-wizard-block-react-app-root', component: WizardBase }
];

const AppWrapper = styled.div`
    position: relative;
    z-index: 1;
    padding: 0;
`;

function MyPagesApp() {
    // debug

    document.removeEventListener('DOMContentLoaded', MyPagesApp);

   window._bazDebug = true; 
    //window._bazDebug = window.__lfSettings.jsDebugEnabled;

    // set moment locale
    moment.locale('sv');

    // redux store
    const store = configureStore();
    window.__bazStore = store;

    // init my pages react "app"
    sections.map((section) => {
        if (section.id) {
            let wrapperEl = document.getElementById(section.id);
            if (wrapperEl) {
                const root = createRoot(wrapperEl)
                root.render(
                    <Provider store={store}>
                        <AppWrapper>
                            {section.component}
                        </AppWrapper>
                    </Provider>
                );
            }
        } else if (section.class) {
            const wrapperEls = document.querySelectorAll('.' + section.class);
            wrapperEls.forEach(element => {
                const root = createRoot(element)
                root.render(
                    <Provider store={store}>
                        <AppWrapper>
                        <section.component element={element} />
                        </AppWrapper>
                    </Provider>
                );
            })
        }
    });
}

document.addEventListener('DOMContentLoaded', MyPagesApp);
